import React, { useState } from "react";
import BLOG_POSTS from "../data/posts.json";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  .research-title {
    background: linear-gradient(110deg, #fbbf24 35%, #a78bfa 50%, #fbbf24 65%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: research-shimmer 8s linear infinite;
  }

  @keyframes research-shimmer {
    0%   { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  .research-dot-grid {
    background-image: radial-gradient(circle, rgba(251,191,36,0.12) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  @keyframes research-fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .research-fade   { animation: research-fadeUp 0.7s ease forwards; }
  .research-fade-1 { animation-delay: 0.05s; opacity: 0; }
  .research-fade-2 { animation-delay: 0.15s; opacity: 0; }
  .research-fade-3 { animation-delay: 0.25s; opacity: 0; }

  @keyframes research-float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%       { transform: translateY(-15px) rotate(-3deg); }
  }
  .research-float { animation: research-float 5s ease-in-out infinite; }

  .research-btn {
    position: relative; overflow: hidden;
    transition: all 0.3s ease;
  }
  .research-btn::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  .research-btn:hover::after { transform: translateX(100%); }
  .research-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(217,119,6,0.35);
    background-color: #d97706;
  }

  .blog-card {
    transition: all 0.35s cubic-bezier(0.23, 1, 0.32, 1);
    position: relative;
    overflow: hidden;
  }
  .blog-card::before {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(251,191,36,0.04), transparent 60%);
    opacity: 0;
    transition: opacity 0.35s ease;
    pointer-events: none;
  }
  .blog-card:hover {
    transform: translateY(-4px);
    border-color: rgba(251,191,36,0.35) !important;
    box-shadow: 0 20px 48px rgba(0,0,0,0.4), 0 0 0 1px rgba(251,191,36,0.1);
  }
  .blog-card:hover::before { opacity: 1; }

  .tag-pill { transition: all 0.2s ease; cursor: pointer; }
  .tag-pill:hover {
    background: rgba(251,191,36,0.15);
    border-color: rgba(251,191,36,0.5);
    color: #fbbf24;
  }
  .tag-pill.active {
    background: rgba(251,191,36,0.2);
    border-color: rgba(251,191,36,0.6);
    color: #fbbf24;
  }

  .page-btn {
    transition: all 0.2s ease;
    min-width: 40px;
    height: 40px;
  }
  .page-btn:hover:not(:disabled) {
    background: rgba(251,191,36,0.15);
    border-color: rgba(251,191,36,0.5);
    color: #fbbf24;
  }
  .page-btn.active {
    background: #d97706;
    border-color: #d97706;
    color: #000;
    font-weight: 700;
  }
  .page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  @keyframes blog-appear {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .blog-appear { animation: blog-appear 0.45s ease forwards; }

  .read-link {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .read-link::after {
    content: '';
    position: absolute;
    bottom: -2px; left: 0;
    width: 0; height: 1px;
    background: currentColor;
    transition: width 0.3s ease;
  }
  .read-link:hover::after { width: 100%; }
`;

/* ─── Icons ──────────────────────────────────────────────────── */
const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const LinkedInIcon  = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);
const InstagramIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);
const MediumIcon    = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/>
  </svg>
);
const TwitterXIcon  = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ICONS = {
  whatsapp:     "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
  file:         "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8",
  arrowRight:   "M5 12h14 M12 5l7 7-7 7",
  chevronLeft:  "M15 18l-6-6 6-6",
  chevronRight: "M9 18l6-6-6-6",
  clock:        "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M12 6v6l4 2",
};

/* ─── Platform config ────────────────────────────────────────── */
const PLATFORM_META = {
  instagram: { label: "Instagram",   color: "text-pink-400",  Icon: InstagramIcon },
  linkedin:  { label: "LinkedIn",    color: "text-sky-400",   Icon: LinkedInIcon  },
  medium:    { label: "Medium",      color: "text-slate-300", Icon: MediumIcon    },
  twitter:   { label: "X / Twitter", color: "text-slate-200", Icon: TwitterXIcon  },
};

const POSTS_PER_PAGE = 6;

/* Derive all unique tags from the JSON */
const ALL_TAGS = ["All", ...Array.from(new Set(BLOG_POSTS.flatMap(p => p.tags)))];

/* Format ISO date → "May 28, 2025" */
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

/* ─── Sub-components ─────────────────────────────────────────── */
const PlatformBadge = ({ platform }) => {
  const meta = PLATFORM_META[platform];
  if (!meta) return null;
  const { label, color, Icon: PIcon } = meta;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${color}
      px-2.5 py-1 rounded-full bg-black/40 border border-white/10 backdrop-blur-sm`}>
      <PIcon size={12} />
      {label}
    </span>
  );
};

const BlogCard = ({ post, index }) => (
  <a
    href={post.url}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={post.title}
    title={post.metaDescription}
    className="blog-card blog-appear flex flex-col rounded-2xl border border-white/8
      bg-gradient-to-br from-slate-900/80 to-slate-950/80 overflow-hidden no-underline"
    style={{ animationDelay: `${index * 0.07}s`, opacity: 0 }}>

    {/* Cover image */}
    <div className="relative h-44 overflow-hidden bg-slate-800 flex-shrink-0">
      <img
        src={post.image}
        alt={post.title}
        loading="lazy"
        className="w-full h-full object-cover opacity-70 hover:opacity-90 transition-opacity duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
      <div className="absolute top-3 left-3">
        <PlatformBadge platform={post.platform} />
      </div>
    </div>

    {/* Body */}
    <div className="p-5 flex flex-col flex-1 gap-3">
      <div className="flex items-center gap-3 text-slate-500 text-xs">
        <span className="flex items-center gap-1">
          <Icon d={ICONS.clock} size={12} />
          {formatDate(post.date)}
        </span>
        <span>·</span>
        <span>{post.readTime}</span>
      </div>

      <h3 className="text-slate-100 font-bold text-base leading-snug line-clamp-2">
        {post.title}
      </h3>

      <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 flex-1">
        {post.excerpt}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {post.tags.map(t => (
          <span key={t} className="text-xs px-2 py-0.5 rounded-full border
            border-violet-800/40 bg-violet-900/10 text-violet-400">
            {t}
          </span>
        ))}
      </div>

      <span className="read-link text-amber-400 text-xs font-semibold mt-1 w-fit">
        Read post
        <Icon d={ICONS.arrowRight} size={13} />
      </span>
    </div>
  </a>
);

/* ─── Main Page ──────────────────────────────────────────────── */
const ArticlePage = ({ setCurrentPage: setNavPage = () => {} }) => {
  const [activePlatform, setActivePlatform] = useState("all");
  const [activeTag,      setActiveTag]      = useState("All");
  const [currentPage,    setCurrentPage]    = useState(1);

  const platforms = [
    { key: "all",       label: "All Platforms" },
    { key: "linkedin",  label: "LinkedIn"      },
    { key: "instagram", label: "Instagram"     },
    { key: "medium",    label: "Medium"        },
    { key: "twitter",   label: "X / Twitter"   },
  ];

  const filtered = BLOG_POSTS.filter(p =>
    (activePlatform === "all" || p.platform === activePlatform) &&
    (activeTag === "All"      || p.tags.includes(activeTag))
  );

  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);
  const paginated  = filtered.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
     currentPage      * POSTS_PER_PAGE
  );

  const handlePlatformChange = (key) => { setActivePlatform(key); setCurrentPage(1); };
  const handleTagChange      = (tag) => { setActiveTag(tag);       setCurrentPage(1); };

  return (
    <>
      <style>{styles}</style>
      <div className="space-y-20 max-w-6xl mx-auto px-4 pb-20">

        {/* ── HERO ──────────────────────────────────────── */}
        <section className="relative pt-16 pb-10 text-center research-fade research-fade-1">
          <div className="research-dot-grid absolute inset-0 rounded-2xl -z-10 opacity-60" />
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border
            border-amber-600/30 bg-amber-900/10 mb-6">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">
              HEKAOS BLOG
            </span>
          </div>

          <div className="mb-8 research-float">
            <Icon d={ICONS.file} size={80} className="text-cyan-400/30 mx-auto" />
          </div>

          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-5">
            <span className="research-title">Articles</span>
          </h1>

          <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
            Technical deep-dives <br/>
            exploring the science and engineering <br/>
            behind intelligence systems.
          </p>
        </section>

        {/* ── BLOG FEED ─────────────────────────────────── */}
        <section className="research-fade research-fade-2 space-y-8">

          {/* Platform tabs */}
          <div className="flex flex-wrap gap-2">
            {platforms.map(({ key, label }) => {
              const meta  = key !== "all" ? PLATFORM_META[key] : null;
              const PIcon = meta?.Icon;
              return (
                <button key={key}
                  onClick={() => handlePlatformChange(key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border
                    text-sm font-semibold transition-all duration-200
                    ${activePlatform === key
                      ? "bg-amber-600 border-amber-600 text-black"
                      : "border-white/10 bg-white/5 text-slate-400 hover:border-amber-600/40 hover:text-amber-300"
                    }`}>
                  {PIcon && <PIcon size={13} />}
                  {label}
                </button>
              );
            })}
          </div>

          {/* Tag pills */}
          <div className="flex flex-wrap gap-2">
            {ALL_TAGS.map(tag => (
              <button key={tag}
                onClick={() => handleTagChange(tag)}
                className={`tag-pill px-3 py-1 rounded-full border text-xs font-medium transition-all
                  ${activeTag === tag
                    ? "active border-amber-600/60 bg-amber-900/20 text-amber-400"
                    : "border-white/10 bg-white/5 text-slate-500"
                  }`}>
                {tag}
              </button>
            ))}
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-slate-500 text-sm">
              {filtered.length === 0
                ? "No posts found"
                : `Showing ${(currentPage - 1) * POSTS_PER_PAGE + 1}–${Math.min(currentPage * POSTS_PER_PAGE, filtered.length)} of ${filtered.length} posts`}
            </p>
            {totalPages > 1 && (
              <p className="text-slate-500 text-sm">Page {currentPage} of {totalPages}</p>
            )}
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              No posts match your current filters.
            </div>
          ) : (
            <div key={`${activePlatform}-${activeTag}-${currentPage}`}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((post, i) => (
                <BlogCard key={post.id} post={post} index={i} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-6">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="page-btn flex items-center justify-center px-3 rounded-lg border
                  border-white/10 bg-white/5 text-slate-400 transition-all">
                <Icon d={ICONS.chevronLeft} size={16} />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`page-btn flex items-center justify-center rounded-lg border text-sm transition-all
                    ${currentPage === page
                      ? "active bg-amber-600 border-amber-600 text-black font-bold"
                      : "border-white/10 bg-white/5 text-slate-400 hover:border-amber-600/40 hover:text-amber-300"
                    }`}>
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="page-btn flex items-center justify-center px-3 rounded-lg border
                  border-white/10 bg-white/5 text-slate-400 transition-all">
                <Icon d={ICONS.chevronRight} size={16} />
              </button>
            </div>
          )}
        </section>

      </div>
    </>
  );
};

export default ArticlePage;